import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Icon, type LucideIconName } from "@/deps/ui-library";
import { api } from "@/api";
import { keys } from "@/api/queries";
import type { CheckoutOrder, SubscriptionStatus } from "@/api/types";
import { useCanIfSignedIn } from "@/app/session";
import { useSubscription } from "@/app/subscription";
import { PaymentPage } from "@/features/settings/subscription/PaymentPage";
import { formatDate } from "@/lib/dates";
import { formatNPR } from "@/lib/money";
import { BILLING_CONTACT } from "@/lib/billingContact";
import { cn } from "@/lib/cn";

/** What one of RigoSub's message codes becomes on screen. */
interface Notice {
  tone: "warn" | "bad" | "info";
  icon: LucideIconName;
  title: string;
  detail: string;
  /**
   * Whether the customer may put it away for the session.
   *
   * Everything softer than grace is dismissible: a bill is due, they know, and a bar they cannot
   * close on every screen for a fortnight is a bar people learn to read past. Grace is not — the
   * next thing that happens is the product refusing them, and a warning they dismissed on Sunday
   * is no warning at all on the Tuesday it lapses.
   */
  dismissible: boolean;
  /** Something is owed that can be paid online, on the payment page (plan slice 10.7). */
  payable?: boolean;
}

/**
 * The message code → banner mapping, whole and pure.
 *
 * `messageCode` is RigoSub's decision, not this SPA's: the API sends `renewal_due` and the SPA
 * renders a renewal notice, and neither side has to agree about what OVERDUE means. That is the
 * point of the code existing alongside the status word, and it is why nothing below reads
 * `status.status` — that vocabulary is RigoSub's, and a product that switched on it would break
 * the day RigoSub added a rung to the ladder.
 *
 * `suspended` is absent on purpose: it is not a banner. `SubscriptionProvider` replaces the whole
 * shell with the suspended screen, so this component never sees one.
 */
export function subscriptionNotice(status: SubscriptionStatus | undefined): Notice | null {
  // Integration off, RigoSub silent, or nothing outstanding. An integration nobody switched on
  // must not look like a subscription that lapsed.
  if (!status || !status.enabled) return null;

  switch (status.messageCode) {
    case "renewal_upcoming":
      // Suite ADR-0082: nothing is unpaid yet — the period simply ends soon, and its renewal bill
      // is raised the day after. The date is RigoSub's; this SPA does no arithmetic on it.
      return {
        tone: "info",
        icon: "CalendarClock",
        title: status.paidThrough
          ? `Your Rigo Accounting subscription is paid up to ${formatDate(status.paidThrough)}`
          : "Your Rigo Accounting subscription is due for renewal soon",
        detail: `The bill for the next period is raised when this one ends. To change your plan before then, write to ${BILLING_CONTACT.email}.`,
        dismissible: true,
      };
    case "renewal_due":
      return {
        tone: "warn",
        icon: "Receipt",
        title: `${formatNPR(status.dueAmount)} is outstanding on your Rigo Accounting subscription`,
        detail: status.dueDate
          ? `Payable by ${formatDate(status.dueDate)}. Settle it with ${BILLING_CONTACT.email} to keep the service running.`
          : `Settle it with ${BILLING_CONTACT.email} to keep the service running.`,
        dismissible: true,
        payable: status.dueAmount > 0,
      };
    case "expiring":
      return {
        tone: "bad",
        icon: "TriangleAlert",
        title: "Your subscription is about to be suspended",
        detail: status.keepAliveUntil
          ? `Access continues until ${formatDate(status.keepAliveUntil)}. After that Rigo Accounting stops until the ${formatNPR(status.dueAmount)} outstanding is settled — call ${BILLING_CONTACT.phone}.`
          : `Rigo Accounting stops shortly unless the ${formatNPR(status.dueAmount)} outstanding is settled — call ${BILLING_CONTACT.phone}.`,
        dismissible: false,
        payable: status.dueAmount > 0,
      };
    case "terminated":
      return {
        tone: "info",
        icon: "Info",
        title: "This subscription has been cancelled",
        detail: `No further bills will be raised. Talk to ${BILLING_CONTACT.email} before the service ends.`,
        dismissible: false,
      };
    case "none":
    case "suspended":
    case "trial":
    case "trial_ended":
      return null;
  }
}

const TONES: Record<Notice["tone"], string> = {
  warn: "bg-warn-soft text-warn-text border-warn/40",
  bad: "bg-bad-soft text-bad-text border-bad/40",
  info: "bg-info-soft text-info-text border-info/40",
};

/** Where a dismissal is remembered. Per tab and per state, both deliberately. */
const DISMISSED_KEY = "rigo.subscription-banner.dismissed";

function readDismissed(): string | null {
  try {
    return globalThis.sessionStorage?.getItem(DISMISSED_KEY) ?? null;
  } catch {
    return null;
  }
}

/**
 * The one billing bar, in the shell, for every non-blocking state.
 *
 * `sessionStorage` keyed by the message code, so dismissing the renewal notice does not also
 * dismiss the grace warning it turns into a week later — the state changed, and the customer has
 * not seen *this* one. And per tab rather than forever: a dismissal is "not now", not "never".
 */
export function SubscriptionBanner() {
  const subscription = useSubscription();
  const notice = subscriptionNotice(subscription?.status);
  // Read once: `sessionStorage` is this tab's, and this component is the only thing that writes
  // it, so re-reading would only ever return what was last written here.
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);
  const can = useCanIfSignedIn();
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<CheckoutOrder | null>(null);
  const [paying, setPaying] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  if (!notice) return null;
  const code = subscription?.status?.messageCode ?? "none";
  if (notice.dismissible && dismissed === code) return null;

  const dismiss = () => {
    try {
      globalThis.sessionStorage?.setItem(DISMISSED_KEY, code);
    } catch {
      // Storage denied: it stays put away for this render and comes back on the next load, which
      // is a better failure than the button doing nothing at all.
    }
    setDismissed(code);
  };

  // An administrator pays what's owed on the payment page, without leaving the screen (plan slice 10.7).
  const payable = !!notice.payable && can("settings.subscription.manage");
  const pay = async () => {
    setPaying(true);
    setRefused(null);
    try {
      setOrder(await api.subscription.payBill());
    } catch (failure) {
      setRefused(failure instanceof Error ? failure.message : "The payment couldn't be started.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div
      role="status"
      className={cn(
        "no-print flex items-start gap-2.5 border-b px-4 py-2.5 text-sm",
        TONES[notice.tone],
      )}
    >
      <span className="flex shrink-0 mt-px">
        <Icon name={notice.icon} size="sm" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{notice.title}</div>
        <div className="opacity-90">{notice.detail}</div>
        {refused ? <div className="font-semibold">{refused}</div> : null}
      </div>
      {payable ? (
        // rds-check-ok: an action inside the billing bar, styled for it like its dismiss control.
        <button
          type="button"
          disabled={paying}
          onClick={() => void pay()}
          className="shrink-0 border-0 bg-transparent p-1 text-sm font-semibold text-current underline cursor-pointer"
        >
          {paying ? "Opening…" : "Pay now"}
        </button>
      ) : null}
      {notice.dismissible && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="shrink-0 flex items-center border-0 bg-transparent cursor-pointer p-1 rounded-md text-current opacity-70 hover:opacity-100"
        >
          <Icon name="X" size="sm" />
        </button>
      )}
      {order ? (
        <PaymentPage
          order={order}
          replace={(method) => api.subscription.payBill(null, method)}
          onClose={() => setOrder(null)}
          onPaid={() => {
            setOrder(null);
            void queryClient.invalidateQueries({ queryKey: keys.subscription });
          }}
        />
      ) : null}
    </div>
  );
}
